import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, WandSparkles, X } from "lucide-react";
import type { FrameElement } from "../../shared/model";
import { useEditor, useUi } from "../editor/react/hooks";
import { contentBox } from "../editor/export";

/** Present mode: a notebook's pages, or the frames, become slides (or the whole board if there are none). */
export function Present({ onExit }: { onExit: () => void }) {
  const ed = useEditor();
  const tool = useUi((ui) => ui.tool);
  const slides = useMemo(() => {
    const pages = ed.pages();
    if (pages.length) return pages.map((p) => ({ title: "", box: { x: p.x, y: p.y, w: p.w, h: p.h } }));
    const shown = ed.doc.all().filter((e) => ed.isShown(e));
    const frames = (shown.filter((e) => e.type === "frame") as FrameElement[]).sort((a, b) => a.y - b.y || a.x - b.x);
    if (frames.length) return frames.map((f) => ({ title: f.title, box: { x: f.x, y: f.y, w: f.w, h: f.h } }));
    const b = contentBox(shown);
    return b ? [{ title: "", box: b }] : [];
  }, [ed]);
  // a notebook starts from the page you are on
  const [i, setI] = useState(() => Math.max(0, ed.pages().indexOf(ed.currentPage()!)));
  const prevTool = useMemo(() => ed.ui.tool, [ed]);

  useEffect(() => {
    ed.clearSelection();
    // slides use the whole screen (only the small control strip at the bottom)
    const saved = ed.camera.insets;
    ed.camera.insets = { left: 0, right: 0, top: 0, bottom: 56 };
    return () => {
      ed.camera.insets = saved;
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
      ed.setTool(prevTool);
    };
  }, [ed, prevTool]);

  useEffect(() => {
    const s = slides[i];
    if (s) ed.camera.animateTo(ed.camera.fitting(s.box, 36, 8), 420);
  }, [ed, i, slides]);

  const exitRef = useRef(onExit);
  exitRef.current = onExit;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        setI((n) => Math.min(slides.length - 1, n + 1));
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        setI((n) => Math.max(0, n - 1));
      } else if (e.key === "Escape") exitRef.current();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [slides.length]);

  return (
    <div className="present-bar" data-canvas-ignore>
      <button type="button" className="icon-btn" onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} aria-label="Previous">
        <ChevronLeft size={20} />
      </button>
      <span className="present-count">
        {slides.length ? `${i + 1} / ${slides.length}` : "empty"}
        {slides[i]?.title ? ` · ${slides[i].title}` : ""}
      </span>
      <button type="button" className="icon-btn" onClick={() => setI((n) => Math.min(slides.length - 1, n + 1))} disabled={i >= slides.length - 1} aria-label="Next">
        <ChevronRight size={20} />
      </button>
      <button type="button" className={"icon-btn" + (tool === "laser" ? " active" : "")} onClick={() => ed.setTool(tool === "laser" ? "hand" : "laser")} aria-label="Laser pointer">
        <WandSparkles size={18} />
      </button>
      <button type="button" className="icon-btn" onClick={onExit} aria-label="Stop presenting">
        <X size={20} />
      </button>
    </div>
  );
}
